import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { getAdminMentorDetailApi, AdminMentorDetail } from "../../features/analytics/api/adminAnalytics.api"
import { Avatar } from "../../shared/ui/Avatar"
import { LineChart, ChartCard } from "./Charts"
import { ChevronLeftIcon, ExclamationTriangleIcon } from "@heroicons/react/16/solid"

const StatCard = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
  <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4">
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900">{typeof value === "number" ? value.toLocaleString("ru") : value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
)

const Stars = ({ rating }: { rating: number }) => (
  <span className="inline-flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={`text-[11px] ${i < Math.floor(rating) ? "text-amber-400" : "text-gray-200"}`}>★</span>
    ))}
    <span className="ml-1 text-xs text-gray-500">{rating}</span>
  </span>
)

export const MentorDetailPage = () => {
  const { mentorId } = useParams<{ mentorId: string }>()
  const [detail, setDetail] = useState<AdminMentorDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    if (!mentorId) return
    setLoading(true)
    setError(false)
    getAdminMentorDetailApi(mentorId)
      .then((res) => setDetail(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [mentorId]) // eslint-disable-line

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить ментора</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading || !detail) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl h-20 animate-pulse" />)}
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl h-48 animate-pulse" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3">
        <Link to="/admin/analytics/mentors"
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-blue-600 transition-colors">
          <ChevronLeftIcon className="w-4 h-4" />
          Менторы
        </Link>
        <span className="text-gray-300">/</span>
        <div className="flex items-center gap-2.5 min-w-0">
          <Avatar name={detail.full_name} src={detail.avatar_url} size={48} />
          <h2 className="text-base font-semibold text-gray-800 truncate">{detail.full_name}</h2>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard label="Просмотров профиля" value={detail.profile_views} />
        <StatCard label="Курсов" value={detail.courses_count} />
        <StatCard label="Просмотров курсов" value={detail.total_views} />
        <StatCard label="Покупок" value={detail.total_purchases} sub={`Конверсия ${detail.avg_conversion_rate}%`} />
        <StatCard label="Выручка" value={`${(detail.total_revenue / 1000).toFixed(0)}K сом`} sub={`Рейтинг ${detail.avg_rating} ★`} />
      </div>

      {/* Views by day (summed across all mentor's courses) */}
      <ChartCard title="Просмотры по дням (сумма по всем курсам)">
        {detail.views_by_day.length > 0 ? (
          <LineChart
            data={detail.views_by_day.map((r) => ({ label: r.date, value: r.count }))}
            color="#3b82f6"
          />
        ) : (
          <div className="flex items-center justify-center h-40">
            <p className="text-sm text-gray-300">Нет данных</p>
          </div>
        )}
      </ChartCard>

      {/* Courses table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b bg-gray-50/60 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Курсы ментора</h3>
          <span className="text-xs text-gray-400">{detail.courses.length} курсов</span>
        </div>

        {detail.courses.length === 0 ? (
          <div className="px-5 py-16 text-center">
            <p className="text-sm text-gray-400">У ментора пока нет курсов</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/40">
                  {["Курс", "Просмотры", "Покупки", "Выручка", "Рейтинг", "Завершаемость"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {detail.courses.map((c) => (
                  <tr key={c.course_id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3.5 font-medium text-gray-800">{c.title}</td>
                    <td className="px-4 py-3.5 text-gray-700">{c.views.toLocaleString("ru")}</td>
                    <td className="px-4 py-3.5 text-gray-700">{c.purchases.toLocaleString("ru")}</td>
                    <td className="px-4 py-3.5 text-gray-700 font-medium whitespace-nowrap">{(c.revenue / 1000).toFixed(0)}K сом</td>
                    <td className="px-4 py-3.5"><Stars rating={c.avg_rating} /></td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${c.completion_rate >= 70 ? "bg-emerald-500" : c.completion_rate >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                            style={{ width: `${c.completion_rate}%` }} />
                        </div>
                        <span className="text-xs text-gray-600">{c.completion_rate}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
